import { InjectionToken } from '@angular/core';

export interface DBConfig {
  name: string;
  version: number;
  objectStoresMeta: ObjectStoreMeta[];
  migrationFactory?: () => {
    [key: number]: (db: IDBDatabase, transaction: IDBTransaction) => void;
  };
}

export interface ObjectStoreMeta {
  store: string;
  storeConfig: {
    keyPath: string | string[];
    autoIncrement: boolean;
    [key: string]: any;
  };
  storeSchema: ObjectStoreSchema[];
}

export interface ObjectStoreSchema {
  name: string;
  keypath: string | string[];
  options: { unique: boolean; [key: string]: any };
}

export interface IndexDetails {
  indexName: string;
  order: string;
}

export interface RequestEvent<T> extends Event {
  target: RequestEventTarget<T>;
}

export interface RequestEventTarget<T> extends EventTarget {
  result: T | T[];
}

export enum DBMode {
  readonly = 'readonly',
  readwrite = 'readwrite',
}

export type Key =
  | string
  | number
  | Date
  | ArrayBufferView
  | ArrayBuffer
  | IDBKeyRange;

export const cacheConfig: DBConfig = {
  name: 'CacheDatabase',
  version: 9,
  objectStoresMeta: [
    {
      store: 'config',
      storeConfig: { keyPath: 'key', autoIncrement: false },
      storeSchema: [
        { name: 'value', keypath: 'value', options: { unique: false } },
      ],
    },
    {
      store: 'actions',
      storeConfig: { keyPath: 'key', autoIncrement: false },
      storeSchema: [
        { name: 'value', keypath: 'value', options: { unique: false } },
      ],
    },
    {
      store: 'menu-links',
      storeConfig: { keyPath: 'key', autoIncrement: false },
      storeSchema: [
        { name: 'value', keypath: 'value', options: { unique: false } },
      ],
    },
    {
      store: 'response',
      storeConfig: { keyPath: 'key', autoIncrement: false },
      storeSchema: [
        { name: 'value', keypath: 'value', options: { unique: false } },
      ],
    },
    {
      store: 'collections',
      storeConfig: { keyPath: 'key', autoIncrement: false },
      storeSchema: [
        { name: 'value', keypath: 'value', options: { unique: false } },
      ],
    },
    {
      store: 'user-menu',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'parentId', keypath: 'parentId', options: { unique: false } },
        {
          name: 'orderIndex',
          keypath: 'orderIndex',
          options: { unique: false },
        },
        {
          name: 'relativePath',
          keypath: 'relativePath',
          options: { unique: false },
        },
        { name: 'sidebar', keypath: 'sidebar', options: { unique: false } },
        { name: 'image', keypath: 'image', options: { unique: false } },
        { name: 'icon', keypath: 'icon', options: { unique: false } },
        { name: 'title', keypath: 'title', options: { unique: false } },
        { name: 'fontColor', keypath: 'fontColor', options: { unique: false } },
        {
          name: 'startGradientColor',
          keypath: 'startGradientColor',
          options: { unique: false },
        },
        {
          name: 'cardBorderStyle',
          keypath: 'cardBorderStyle',
          options: { unique: false },
        },
        {
          name: 'cardIconStyle',
          keypath: 'cardIconStyle',
          options: { unique: false },
        },
        { name: 'groups', keypath: 'groups', options: { unique: false } },
        {
          name: 'tooltipVisible',
          keypath: 'tooltipVisible',
          options: { unique: false },
        },
        {
          name: 'countScript',
          keypath: 'countScript',
          options: { unique: false },
        },
        {
          name: 'borderVisible',
          keypath: 'borderVisible',
          options: { unique: false },
        },
        { name: 'params', keypath: 'params', options: { unique: false } },
        {
          name: 'fileArchiveId',
          keypath: 'fileArchiveId',
          options: { unique: false },
        },
        {
          name: 'popupSize',
          keypath: 'popupSize',
          options: { unique: false },
        },
        {
          name: 'allowFavorite',
          keypath: 'allowFavorite',
          options: { unique: false },
        },
        {
          name: 'isFavorite',
          keypath: 'isFavorite',
          options: { unique: false },
        },
      ],
    },
    {
      store: 'layouts',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'title', keypath: 'title', options: { unique: false } },
        { name: 'subTitle', keypath: 'subTitle', options: { unique: false } },
        {
          name: 'shortTitle',
          keypath: 'shortTitle',
          options: { unique: false },
        },
        { name: 'icon', keypath: 'icon', options: { unique: false } },
        {
          name: 'startGradientColor',
          keypath: 'startGradientColor',
          options: { unique: false },
        },
        { name: 'fontColor', keypath: 'fontColor', options: { unique: false } },
        {
          name: 'inheritsColors',
          keypath: 'inheritsColors',
          options: { unique: false },
        },
      ],
    },
  ],
};

export const CONFIG_TOKEN = new InjectionToken<DBConfig>('cacheDb', {
  factory: () => {
    return cacheConfig;
  },
});
